import { flow } from '@1x2network/flow'
import { BasicLayout, OrientationMap, Orientor, scaleToLongest } from '@1x2network/layout'
import {
    Component,
    ContainerSpecification,
    createSpine,
    createSprite,
    Spine,
    SpineSpecification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { Container, Sprite } from 'pixi.js'

/**
 * Background sprite for the game, with a tiered spine animation on top. Animation changes as the excitement changes.
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class Background extends Component<BackgroundSpec> {
    private spine: Spine
    private sprites: Orientor<Sprite>
    private layout: BasicLayout
    private isPlayingWin = false

    constructor(spec: string, parent: Container, layout: BasicLayout) {
        super({ spec, parent })
        this.layout = layout
        this.spine = createSpine({ spec: this.specification.spine })
        this.sprites = new Orientor({
            layout,
            spec: this.specification.sprites,
            create: (spec: SpriteSpecification) => createSprite({ spec, parent: this }),
            onOrient: (active, inactive) => {
                active.addChild(this.spine)
                active.visible = true
                inactive.visible = false
            },
        })
        layout.onResize.connect(this.reposition.bind(this))
        this.reposition()
    }

    public playIdle(): void {
        const animation = this.specification.idle[this.layout.orientation]
        this.spine.play(animation, 0, true)
    }

    async playScatter() {
        const animation = this.specification.wins[this.layout.orientation]
        await flow.wait(this.spine.play(animation))
        this.playIdle()
    }

    async playWin() {
        this.isPlayingWin = true
        const animation = this.specification.wins[this.layout.orientation]
        while (this.isPlayingWin) {
            await flow.wait(this.spine.play(animation))
        }
        this.playIdle()
    }

    public stopWin() {
        this.isPlayingWin = false
    }

    reposition() {
        const { size, orientation, left, right, top, bottom } = this.layout
        const centre = { x: (left + right) / 2, y: (top + bottom) / 2 }

        // Scale and position the sprites
        this.sprites.call((sprite) => {
            const spriteSize = { width: sprite.texture.width, height: sprite.texture.height }
            sprite.scale.set(scaleToLongest(spriteSize, size))
            sprite.position.copyFrom(centre)
        })

        this.spine.y = this.specification.spine[orientation].y || 0
    }

    public reset() {
        this.spine.reset()
    }
}

export interface BackgroundSpec extends ContainerSpecification {
    spine: SpineSpecification & OrientationMap<ContainerSpecification>
    wins: OrientationMap<string>
    idle: OrientationMap<string>
    sprites: OrientationMap<SpriteSpecification>
    size: OrientationMap<{ height: number; width: number }>
}
